import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://code7s.com";
const DEFAULT_IMAGE = `${SITE_URL}/android-chrome-512x512.png`;

const metaByPath = {
  "/": {
    title: "Code7s | Startup Product Studio for Apps, Websites and SaaS",
    description:
      "Code7s is a startup product studio that helps founders design, develop and launch mobile apps, websites, SaaS platforms, MVPs and original Code7s products.",
  },
  "/services": {
    title: "Startup Product Design, Web Development and App Development Services | Code7s",
    description:
      "Explore Code7s services for startups and founders, including product strategy, UX UI design, mobile app development, web development, SaaS development, MVP builds and launch support.",
  },
  "/products": {
    title: "Apps, Tools and Digital Products Published by Code7s",
    description:
      "Discover products published by Code7s, including productivity apps, finance apps, document search tools, mobile games and studio tools built with premium design and modern engineering.",
  },
  "/portfolio": {
    title: "Portfolio | Apps, Websites and SaaS Platforms Built by Code7s",
    description:
      "See selected Code7s work across startup MVPs, mobile apps, websites, SaaS interfaces, business dashboards, ecommerce platforms and original Code7s products.",
  },
  "/about": {
    title: "About Code7s | Startup Product Studio and App Publisher",
    description:
      "Learn how Code7s helps startups, founders and businesses turn ideas into polished software products through strategy, product design, development and launch support.",
  },
  "/contact": {
    title: "Contact Code7s | Build Your Startup App, Website or SaaS Product",
    description:
      "Contact Code7s to design, develop and launch your startup MVP, mobile app, website, SaaS platform, ecommerce product, dashboard or digital tool.",
  },
  "/privacy-policy": {
    title: "Privacy Policy Center | Code7s",
    description:
      "Choose a Code7s privacy policy, including the Code7s website and studio privacy policy or the Sudoku app privacy policy.",
  },
  "/privacy-policy/code7s": {
    title: "Code7s Privacy Policy",
    description:
      "Read the Code7s privacy policy for our website, startup product studio services, client projects, and products published by Code7s.",
  },
  "/privacy-policy/sudoku": {
    title: "Sudoku App Privacy Policy | Code7s",
    description:
      "Read the privacy policy for the Sudoku app published by Code7s, including gameplay data, analytics, diagnostics, children privacy, and contact information.",
  },
  "/services/mobile": {
    title: "Mobile App Development for Startups and Founders | Code7s",
    description:
      "Code7s designs and develops premium iOS and Android apps for startups, founders and product led businesses, from MVP planning and UX design to app launch support.",
  },
  "/services/web": {
    title: "Web Development and SaaS Development for Startups | Code7s",
    description:
      "Build modern startup websites, SaaS platforms, landing pages, dashboards, portals and web applications with Code7s product design and engineering.",
  },
  "/services/uiux": {
    title: "Product Design and UX UI Design for Startup Products | Code7s",
    description:
      "Code7s creates product strategy, UX flows, UI design systems, prototypes and launch ready product interfaces for apps, websites, SaaS platforms and MVPs.",
  },
  "/portfolio/shree": {
    title: "Shree Electronics Ecommerce Platform Case Study | Code7s",
    description:
      "Explore the Shree Electronics ecommerce case study by Code7s, including product design, responsive web development and online retail platform execution.",
  },
  "/portfolio/empire": {
    title: "Empire Dashboard Business Platform Case Study | Code7s",
    description:
      "Explore the Empire Dashboard case study by Code7s, a business dashboard and operations platform designed for data visibility, workflow clarity and decision making.",
  },
  "/portfolio/landing-page": {
    title: "Startup Landing Page Design Case Study | Code7s",
    description:
      "See how Code7s designs premium startup landing pages for SaaS products, MVP launches, conversion focused campaigns and modern brand presentation.",
  },
};

const detailMeta = {
  "/products/spendnote": {
    title: "SpendNote Expense Tracking App by Code7s",
    description:
      "SpendNote is an expense tracking app by Code7s built for fast entries, receipt storage, budgeting, simple personal finance workflows and clean mobile usability.",
  },
  "/products/scora": {
    title: "Scora Document Search and Knowledge App by Code7s",
    description:
      "Scora is a document search and knowledge app by Code7s for finding information inside PDFs, scans, files and notes with fast search and organized workflows.",
  },
  "/products/gridlock": {
    title: "Grid Lock Mobile Puzzle Game by Code7s",
    description:
      "Grid Lock is a retro inspired mobile puzzle game by Code7s, designed with smooth controls, focused gameplay and a compact mobile game experience.",
  },
  "/products/tools": {
    title: "Code7s Studio Tools for Startup Product Planning",
    description:
      "Code7s Studio Tools help founders, freelancers and product teams prototype ideas, map user flows, plan MVPs and visualize software products before development.",
  },
  "/products/nova": {
    title: "Project Nova Product Management Tool by Code7s",
    description:
      "Project Nova is a product management and whiteboarding concept by Code7s for startup teams planning roadmaps, tasks, product decisions and collaboration.",
  },
};

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
}

export default function SEO() {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPath = pathname.replace(/\/$/, "") || "/";
    const routeMeta = metaByPath[normalizedPath] || detailMeta[normalizedPath] || metaByPath["/"];

    const canonical = `${SITE_URL}${normalizedPath === "/" ? "" : normalizedPath}`;

    document.title = routeMeta.title;
    upsertCanonical(canonical);
    upsertMeta('meta[name="description"]', {
      name: "description",
      content: routeMeta.description,
    });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: "index, follow, max-image-preview:large",
    });
    upsertMeta('meta[property="og:type"]', {
      property: "og:type",
      content: "website",
    });
    upsertMeta('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: "Code7s",
    });
    upsertMeta('meta[property="og:title"]', {
      property: "og:title",
      content: routeMeta.title,
    });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: routeMeta.description,
    });
    upsertMeta('meta[property="og:url"]', {
      property: "og:url",
      content: canonical,
    });
    upsertMeta('meta[property="og:image"]', {
      property: "og:image",
      content: DEFAULT_IMAGE,
    });
    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: routeMeta.title,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: routeMeta.description,
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: DEFAULT_IMAGE,
    });
  }, [pathname]);

  return null;
}
